/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable jsdoc/require-returns */

import { EventEmitter, Injectable } from "@angular/core";
import { ProjectActions, ProjectSelectors } from "@app/project/store";
import { User } from "@app/user/model/user";
import { UserManagementActions, UserManagementSelectors } from "@app/user/store";
import { Action, Store, select } from "@ngrx/store";
import { Observable, combineLatest, of } from "rxjs";
import { catchError, distinctUntilChanged, filter, map, take, tap, withLatestFrom } from "rxjs/operators";
import { Project, UserinfoKeyType } from "../model/project";
import { getActiveProjectCellularProvider, getActiveProjectSecuritySystem } from "../store/selectors/projects.selectors";

import { ActivatedRouteSnapshot } from "@angular/router";
import { Address } from "@app/address/model/address";
import { LOG_CATEGORY, LOG_SUB_TYPE, LOG_TYPE, createAuditLogKey } from "@app/audit-log/services/audit-log.constants";
import { AuditLogService } from "@app/audit-log/services/audit-log.service";
import { CellularProviderStatuses } from "@app/cellular/models/cellular-device.model";
import { CellularService } from "@app/cellular/services/cellular.service";
import { CustomerLocationActions } from "@app/customer-portal/store/actions";
import { AlulaDevice } from "@app/project/model/alula-device";
import { AlulaActions, ProjectFeatureActions } from "@app/project/store/actions";
import { ProjectDispatchers } from "@app/project/store/dispatchers/project.dispatchers";
import { SnackbarActions } from "@app/root-store/actions";
import { ApiResultState, LoadingState } from "@app/root-store/api-result.state";
import { RouterSelectors } from "@app/root-store/selectors";
import { ClareDialogComponent, IDialogData } from "@app/shared/components/clare-dialog/clare-dialog.component";
import { CellularProviderType } from "@app/shared/databases/cellular.database.service";
import { SendEmailDatabaseService } from "@app/shared/databases/email.database";
import { ActionWord } from "@app/shared/resources/action-word";
import { EquatorProjectinfoService } from "@app/shared/services/equator-api/equator-projectinfo/equator-projectinfo.service";
import { HttpResponseHandler } from "@app/shared/services/http-response-handler";
import { DateTime } from "luxon";
import { CustomerAction } from "../../customer/store/actions/customer.actions";
import { TSLoggingService } from "../../shared/services/ts-logging.service";
import { ClareSystem } from "../model";
import { PanelSummary, getDefaultPanelSummary } from "../model/panel-summary";
import { AdditionalFeature } from "../model/project-feature";
import { ProjectListItem } from "../model/project-list-item";
import { SecuritySystem } from "../model/security-system";
import { AlulaDispatchers } from "../store/dispatchers/alula.dispatchers";
import { DeviceDispatchers } from "../store/dispatchers/device.dispatchers";
import { DispatchDeleteProjectArgs } from "../store/dispatchers/project.dispatchers";
import { ProjectState } from "../store/projects.state";
import { AlulaDeviceDbService } from "./alula-device-db.service";
import { ProjectHelperService } from "./project-helper.service";

@Injectable({
    providedIn: "root",
})
export class ProjectService {
    constructor(
        private readonly projectDispatchers: ProjectDispatchers,
        private readonly projectInfoService: EquatorProjectinfoService,
        private readonly store: Store<ProjectState>,
        private readonly alulaService: AlulaDeviceDbService,
        private readonly tsLoggingService: TSLoggingService,
        private readonly cellularService: CellularService,
        private readonly emailService: SendEmailDatabaseService,
        private readonly deviceDispatchers: DeviceDispatchers,
        private readonly alulaDispatchers: AlulaDispatchers,
        private auditLogService: AuditLogService
    ) {}

    clearActiveProject() {
        this.projectDispatchers.clearActiveProject();
    }

    clearAllDCOs() {
        this.deviceDispatchers.clearAllDCOs();
    }

    dispatch(action: Action) {
        return this.store.dispatch(action);
    }
    /**
     * Get the active project from the state
     *
     * @param hasDispatchWhenEmpty
     * @description Get the active project from the state or load it from the database
     * if it is not already in the state
     */
    getActiveProject$(hasDispatchWhenEmpty = true, uuid?: string): Observable<Project> {
        return this.store.pipe(
            select(ProjectSelectors.getActiveProjectState),
            distinctUntilChanged(),
            withLatestFrom(this.store.pipe(select(ProjectSelectors.getActiveProjectId))),
            tap(([projectState, projectId]: [any, number]) => {
                if (projectState.callState === LoadingState.init && projectId && hasDispatchWhenEmpty) {
                    this.store.dispatch(new ProjectActions.GetActiveProject(projectId));
                } else if (!projectId && uuid) {
                    this.store.dispatch(new ProjectActions.GetActiveProjectByUuid(uuid));
                }
            }),
            filter(([state]) => state.callState === LoadingState.loaded),
            map(([projectState]) => projectState.result),
            filter(project => !!project)
        );
    }

    /**
     * Get the active project from the state
     *
     * @param projectUuid The uuid of the project to look up
     * @description Get the active project from the state or load it from the database
     * if it is not already in the state
     */
    getActiveProjectByUuid$(projectUuid: string): Observable<Project> {
        return this.store.pipe(
            select(ProjectSelectors.getActiveProjectState),
            tap(state => {
                if (state.callState === LoadingState.init) {
                    this.store.dispatch(new ProjectActions.GetActiveProjectByUuid(projectUuid));
                }
            }),
            map(projectState => projectState.result),
            filter(project => !!project)
        );
    }

    /**
     * Get the project's users
     *
     * @param projectId The ID of the project to look up
     * Get the project users from the state or load them from the database if
     * they are not already in the state
     */
    getProjectUsers$(projectId: number): Observable<User[]> {
        return this.store.pipe(
            select(ProjectSelectors.getProjectUsersState(projectId)),
            tap(state => {
                if (state.callState === LoadingState.init) {
                    this.projectDispatchers.dispatchProjectUsers(projectId);
                }
            }),
            filter(state => !!state.result),
            map(state => state.result)
        );
    }

    /**
     * Get the active project's users
     *
     * @param num The numeric suffix to add to "userinfo" to get the requested user.
     * Get the active project's primary and secondary contacts
     * This data comes from the userinfo1, userinfo2, userinfo3 fields on the project object.
     * This is not the list of mobile app users.
     */
    getActiveProjectUser$(num: 1 | 2 | 3 = 1): Observable<User> {
        return combineLatest([
            this.store.pipe(select(UserManagementSelectors.getActiveProjectUserState)),
            this.store.pipe(select(ProjectSelectors.getActiveProject)),
        ]).pipe(
            tap(([userState, activeProject]) => {
                if (userState.callState === LoadingState.init && activeProject) {
                    this.store.dispatch(
                        UserManagementActions.UserActions.loadActiveProjectUser({ userId: activeProject[`userinfo${num}`] })
                    );
                }
            }),
            map(([userState]) => userState?.result || null)
        );
    }

    getActiveProjectFeatures$() {
        return combineLatest({
            projectId: this.store.pipe(select(ProjectSelectors.getActiveProjectId)),
            projectFeatureState: this.store.pipe(select(ProjectSelectors.getActiveProjectFeatures())),
        }).pipe(
            filter(({ projectId }) => !!projectId),
            tap(({ projectId, projectFeatureState }) => {
                if (projectFeatureState.callState === LoadingState.init) {
                    this.store.dispatch(ProjectFeatureActions.load({ projectId }));
                }
            }),
            map(({ projectFeatureState }) => projectFeatureState),
            filter(state => state.callState === LoadingState.loaded),
            map(state => state.result)
        );
    }

    getAssignedProjectFeature(): Observable<AdditionalFeature[]> {
        return combineLatest({
            featureObj: this.getAllProjectFeatures().pipe(take(1)),
            assignedFeatureList: this.getActiveProjectFeatures$().pipe(distinctUntilChanged()),
        }).pipe(
            map(({ featureObj, assignedFeatureList }) =>
                Object.values(featureObj || {}).filter(
                    feature => !!assignedFeatureList?.find(assigned => assigned.featureId === feature.id)
                )
            )
        );
    }

    getAssignedProjectFeatureByKey$(featureKey: string): Observable<AdditionalFeature> {
        return combineLatest({
            featureObj: this.getAllProjectFeatures().pipe(take(1)),
            assignedFeatureList: this.getActiveProjectFeatures$().pipe(distinctUntilChanged()),
        }).pipe(
            map(({ featureObj, assignedFeatureList }) => {
                const foundFeature = Object.values(featureObj || {}).find(feature => feature.featureKey === featureKey);
                if (!foundFeature) {
                    return null;
                }
                const assigned = assignedFeatureList.find(assigned => assigned.featureId === foundFeature.id);
                return assigned ? foundFeature : null;
            })
        );
    }

    getAllProjectFeatures() {
        return this.store.pipe(select(ProjectSelectors.getAllAdditionalFeatures())).pipe(
            tap(allAdditionalFeatures => {
                if (allAdditionalFeatures.callState === LoadingState.init) {
                    this.store.dispatch(ProjectFeatureActions.loadAllAdditionalFeatures.request(null));
                }
            }),
            filter(state => state.callState === LoadingState.loaded),
            map(state => state.result)
        );
    }

    selectAlulaDevice$() {
        return this.alulaDispatchers.selectAlulaDevice$().pipe(
            distinctUntilChanged(),
            filter(deviceState => deviceState?.callState === LoadingState.loaded)
        );
    }

    deactivateServicePlan(alulaDeviceCall: ApiResultState<AlulaDevice>, project: Project, completeEmitter?: EventEmitter<any>) {
        if (alulaDeviceCall?.result?.isMonitoringServiceEnabled()) {
            this.alulaDispatchers.clearAlulaState();
            const payload = {
                device: alulaDeviceCall.result,
                emitter: completeEmitter,
                entityId: project.entityId,
                projectId: project.id,
            };
            this.projectDispatchers.dispatch(new AlulaActions.DisableSecurityMonitoring(payload));
        }
    }

    dispatchUpdateProjectDealer(projectData, projectId, closeDialog, customer, logData = null) {
        this.projectDispatchers.dispatchUpdateProjectDealer(projectData, projectId, closeDialog, customer, logData);
    }

    /**
     * Get the active project's users
     *
     * Get the active project's primary contact
     * This data comes from the userinfo1, which is loaded when loading project detail
     * This is not the list of mobile app users.
     */
    getActiveProjectPrimaryUser$(): Observable<User> {
        return this.store.pipe(select(UserManagementSelectors.getActiveProjectUserState)).pipe(map(userState => userState?.result || null));
    }

    /**
     * Dispatch the calls to get the project ID to UUID used in project status
     *
     * @param entityId
     * @description Get all the project IDs and UUIDs for the currently authenticated user's
     * entity and subentities. Returns the entity ID, but it is irrelevant.
     */
    getProjectsUUIDs(entityId?: number): Observable<number> {
        return this.projectDispatchers.loadAuthUserProjectsUUIDs$(entityId);
    }

    /**
     * Get a child route in the current active route
     *
     * @param depth How deep into the routing to search
     * @description Get a child route snapshot in the current active route
     */
    getChildRoute(depth: number): Observable<ActivatedRouteSnapshot> {
        return this.store.pipe(select(RouterSelectors.getChildRoute(depth)));
    }

    /**
     * Convert the Alula device to a security device
     *
     * @param alulaDevice The Alula device to convert
     * @description Create a new security system device from the information in an Alula device
     * response from their API
     */
    convertAlulaDeviceToSecurityDevice(alulaDevice: AlulaDevice): SecuritySystem {
        return new SecuritySystem(alulaDevice.macAddress, "security");
    }

    /**
     * get security system from active project
     */
    getActiveSecuritySystem() {
        return this.store.select(getActiveProjectSecuritySystem);
    }

    /**
     * get security system from active project
     */
    getActiveProjectCellularProvider() {
        return this.store.select(getActiveProjectCellularProvider);
    }

    /**
     * Display a user prompt before deleting a project
     *
     * @param project The project to delete
     * @param closeDialog
     * @param confirmation
     * @param args
     * @description Open a Mat Dialog to prompt the user before proceeding to delete a project
     */
    deleteProjectConfirmation(args: DispatchDeleteProjectArgs): void {
        this.projectDispatchers.deleteProjectConfirmation(args);
    }

    /**
     * Send the coordinates to ClareHome
     *
     * @param uuid Project controller UUID
     * @param address The address object that contains the coordinates
     * @param project
     * @description Send coordinates to ClareHome
     */
    sendCoordinatesToClareHome(uuid: string, address: Address, project?: Project) {
        return this.projectInfoService.sendCoordinates(uuid, address).pipe(
            take(1),
            tap(result => {
                if (!result) {
                    this.store.dispatch(
                        SnackbarActions.openSnackbar({
                            message: new HttpResponseHandler("address coordinates", ActionWord.saved, null, true),
                        })
                    );
                } else {
                    this.store.dispatch(
                        SnackbarActions.openSnackbar({
                            message: new HttpResponseHandler("address coordinates", ActionWord.saved),
                        })
                    );
                    this.store.dispatch(new CustomerLocationActions.GetLocationData({ uuid }));
                }
                const logKey = createAuditLogKey(LOG_CATEGORY.ACCOUNT, LOG_TYPE.ACCOUNT, LOG_SUB_TYPE.SEND_COORDINATES);
                this.auditLogService.logEvent(logKey, {
                    data: `zipcode: ${address.zipcode}, coordinates: ${address.coordinates}`,
                    entityId: project?.entityId,
                    projectId: project?.id,
                    optionalParams: { uuid },
                });
            })
        );
    }

    updateCoordinates(project: Project, claresystem: ClareSystem, address: Address) {
        if (address?.coordinates) {
            this.sendCoordinatesToClareHome(claresystem?.uuid, address, project).subscribe();
        } else {
            const dialogData: IDialogData = {
                title: "No coordinates available",
                message: "Please edit the address and perform a lookup before sending coordinates to ClareHome.",
                buttons: [
                    {
                        text: "Close",
                        method: "close",
                    },
                ],
            };

            this.showNoCoordinatesInAddressDialog({
                componentOrTemplateRef: ClareDialogComponent,
                config: {
                    id: "no-coordinates",
                    data: dialogData,
                },
            });
        }
    }

    /**
     * Display a dialog when there are no coordinates
     *
     * @param payload The component and dialog config
     * Display a user prompt to the user when the coordinates have not been sent to ClareHome yet.
     */
    showNoCoordinatesInAddressDialog(payload): void {
        this.projectDispatchers.showNoCoordinatesInAddressDialog(payload);
    }

    /**
     * display success message
     *
     * @param project the entity need to be updated
     * @param projectData
     */
    removeProjectSuccess(projectData: Project) {
        this.projectDispatchers.removeProjectSuccess(projectData);
    }

    /**
     * check project cellular connetivity
     *
     * @param securitySystem
     * @param securitySystem.imei
     * @param securitySystem.iccid
     * @param securitySystem.cellularProvider
     * @returns
     */
    isCellularActive(securitySystem: { imei?: string; iccid?: string; cellularProvider?: CellularProviderType }): Observable<boolean> {
        if (securitySystem && (securitySystem.imei || securitySystem.iccid) && securitySystem.cellularProvider) {
            return this.cellularService
                .fetchCellularDevice(securitySystem.iccid, securitySystem.cellularProvider)
                .pipe(map(dev => this.cellularService.getCellularDeviceStatus(dev) === CellularProviderStatuses.Active));
        }

        return of(false);
    }

    /**
     * check project cellular connetivity
     *
     * @param emei
     * @param macAddress
     * @returns
     */
    isAlulaActive(macAddress: string): Observable<boolean> {
        if (macAddress) {
            return this.alulaService.getDeviceByMacAddress({ macAddress }).pipe(map(dev => !!dev?.customerId));
        }

        return of(false);
    }

    getProjectList$(): Observable<ProjectListItem[]> {
        return this.store.pipe(
            select(ProjectSelectors.getFilteredProjectListData as any),
            catchError(error => {
                console.log(error);
                return of([]);
            })
        );
    }

    getProjectCallState$() {
        return this.store.pipe(select(ProjectSelectors.getProjectsCallState));
    }

    getActiveProjectStatus$() {
        return this.projectDispatchers.selectActiveProjectStatus$();
    }

    getMacAddress(project: Project, uuid, unsubscribe$, isProjectOnline) {
        return this.projectDispatchers.getMacAddress(
            ProjectHelperService.getDbSecuritySystemMacAddress(project),
            uuid,
            unsubscribe$,
            isProjectOnline
        );
    }

    getAlulaDevice$(mac, unsubscribe$) {
        return this.projectDispatchers.getAlulaDevice(mac, unsubscribe$).pipe(
            tap(devices => {
                if (devices.result) {
                    // if there is a device, get the panel's Alula user
                    this.projectDispatchers.dispatch(
                        new AlulaActions.GetAlulaUserByPartialUsername({
                            partialUsername: devices.result.macAddress || mac,
                        })
                    );
                }
            })
        );
    }

    getActiveProjectEntity$() {
        return this.projectDispatchers.getActiveProjectEntity$();
    }

    upsertCustomer(action: CustomerAction, project: Project, userInfoKey: UserinfoKeyType, closeDialog?: EventEmitter<any>) {
        action.callbackEmitter = {
            emit: _action => {
                this.dispatchCreateProjectUserAndGetActiveProjectUser(_action, project, userInfoKey, closeDialog);
            },
        } as EventEmitter<any>;
        this.projectDispatchers.dispatch(action);
    }

    sendPINUpdateEmail(isPMMoveOutUpdate = false) {
        this.getActiveProjectUser$()
            .pipe(
                withLatestFrom(this.getActiveProjectEntity$(), this.getActiveProject$()),
                take(1),
                tap(([user, entity, project]) =>
                    this.emailService.sendEmailRequest$(
                        user.email,
                        `${user.firstName} ${user.lastName}`,
                        $localize`:@@ACCOUNT.DEVICE.SECURITY_ALERT:Security alert`,
                        // eslint-disable-next-line max-len
                        $localize`:@@ACCOUNT.DEVICE.SECURITY_ALERT_EMAIL:Your ${
                            isPMMoveOutUpdate ? "property manager" : "security dealer"
                        } has made changes to your ClareOne Master User PIN for the Location "${
                            project.claresystem?.name
                        }". Please ignore this email if you requested this change. Otherwise please contact your ${
                            isPMMoveOutUpdate ? "property manager" : "security dealer "
                        } for assistance,
                    ${entity.name}.`
                    )
                )
            )
            .subscribe();
    }

    private dispatchCreateProjectUserAndGetActiveProjectUser(
        action: CustomerAction,
        project: Project,
        userInfoKey: UserinfoKeyType,
        closeDialog?: EventEmitter<void>
    ) {
        const projectUserData = {
            userData: action.customer,
            project,
            userInfoKey,
        };

        this.projectDispatchers.dispatch(new ProjectActions.CreateProjectUser(projectUserData, closeDialog));
        this.projectDispatchers.dispatch(UserManagementActions.UserActions.loadActiveProjectUserSuccess({ user: action.customer }));
    }

    getActivePanelSummaryState(uuid: string): Observable<ApiResultState<PanelSummary>> {
        if (!uuid) {
            return of({
                callState: LoadingState.loaded,
                result: getDefaultPanelSummary(),
            });
        }

        return this.store.pipe(select(ProjectSelectors.getActivePanelSummaryState())).pipe(
            tap(summaryState => {
                if (summaryState.callState === LoadingState.init) {
                    this.store.dispatch(new ProjectActions.GetActivePanelSummary({ uuid }));
                }
            })
        );
    }

    clearActivePanelSummaryState() {
        this.store.dispatch(new ProjectActions.ClearActivePanelSummary());
    }

    updateProjectLockoutTime(project: Project, isLockedOut: boolean): void {
        this.projectDispatchers.dispatchUpdateProject(
            { ...project, lockoutTime: isLockedOut ? DateTime.now().toISO() : null },
            project.id,
            null
        );
    }
}
